// Unpublished Work © 2025 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Typography, MultiSelect, Stack, AssistanceTooltip, TableContainer, Table, TableRow, TableCell, TableHead, TableBody, Box} from '@deere/isg.component-library';
import {IconInfoAlert} from '@deere/icons';
import {useDeepMemo} from 'Utils/react-utils';
import {MOWING_DIRECTION_REMAPPING} from 'OnLabor/workboard/constants/mowing-patterns';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {
    BACKTRACK_CUT,
    DOUBLE_CUT,
    EXCLUDED_MOWING_DIRECTIONS_FOR_CUT,
    SINGLE_CUT
} from 'OnLabor/workboard/constants/cutting-styles';
import {TWELVE_TO_SIX} from 'OnLabor/workboard/constants/mowing-directions';
import moment from 'moment';
import {DATE_FORMATS} from 'Utils/time-utils';

const CUTS_WITH_2_INPUTS = [BACKTRACK_CUT, DOUBLE_CUT];

function getMowingDirection(mowingDirection) {
    return MOWING_DIRECTION_REMAPPING[mowingDirection] || mowingDirection;
}

function getExcludedMowingDirections(cuttingStyle) {
    return EXCLUDED_MOWING_DIRECTIONS_FOR_CUT[cuttingStyle] || [];
}

function resetSelections(cuttingStyle, firstDirectionId, onChange) {
    if (!CUTS_WITH_2_INPUTS.includes(cuttingStyle)) {
        onChange('direction2', null);
    }

    onChange('direction', firstDirectionId);
}

function getLastInfo(cutHistory, name) {
    if (!cutHistory || !cutHistory[0][name]) {
        return '---';
    }
    return cutHistory[0][name];
}

function getCutHistoryTable(cutHistory, translations) {
    if (!cutHistory) {
        return (
            <Box
                alignItems='center'
                bgcolor='#F1F0F0'
                display='flex'
                flexDirection='column'
                padding='10px'
                width='100%'
            >
                <Typography
                    gutterBottom
                    variant='labelLargeSemibold'
                >
                    {translations.ONLINK_NO_CUT_HISTORY_HEADER}
                </Typography>
                <Typography variant='bodyMedium'>
                    {translations.ONLINK_NO_CUT_HISTORY_BODY}
                </Typography>
            </Box>
        );
    }
    return (
        <Box
            sx={{
                overflowX: {
                    xs: 'auto',
                    md: 'visible'
                },
                WebkitOverflowScrolling: {
                    xs: 'touch',
                    md: 'auto'
                },
                maxWidth: {
                    xs: 320,
                    md: 'none'
                }
            }}
        >
            <TableContainer
                sx={{
                    boxShadow: 'none'
                }}
            >
                <Table>
                    <TableHead
                        sx={{
                            '& .MuiTableCell-head': {
                                borderBottom: '1px solid #5e5e5d !important'
                            }
                        }}
                    >
                        <TableRow>
                            <TableCell>{translations.DATE}</TableCell>
                            <TableCell>{translations.ONLINK_CUT_TYPE}</TableCell>
                            <TableCell>{translations.ONLINK_MOWING_DIRECTION}</TableCell>
                            <TableCell>{translations.ONLINK_CLEANUP_PASS}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={{
                            '& tr:last-of-type td, & tr:last-of-type th': {
                                borderBottom: 'none'
                            }
                        }}
                    >
                        {cutHistory.map((record, i) => {
                            return (
                                <TableRow key={i}>
                                    <TableCell>
                                        <div>
                                            {moment(record.dateSelect, DATE_FORMATS.day).format('MMM DD, YYYY')}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div>
                                            {record.cuttingStyle}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div>
                                            {[record.direction, record.direction2].filter(Boolean).join(', ')}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div>
                                            {record.cleanupPass ?? '---'}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function CutsAndDirectionsItem(props) {
    const {
        loading,
        readOnly,
        membership,
        mowingPattern,
        cutHistory,
        onChange,
        translations
    } = props;

    const direction1 = getMowingDirection(mowingPattern?.direction);
    const direction2 = getMowingDirection(mowingPattern?.direction2);

    const excludedMowingDirections = getExcludedMowingDirections(mowingPattern?.cuttingStyle);

    const filteredMowingDirections = useDeepMemo(() => membership?.mowingDirections.filter((mowingDirection) => {
        const {
            areasApplied,
            direction,
            inactive
        } = mowingDirection;

        return direction === direction1 || direction === direction2 || !inactive && areasApplied.includes(mowingPattern?.area);
    }, []), [direction1, direction2, membership?.mowingDirections, mowingPattern?.area]);

    const directionItems = useDeepMemo(() => filteredMowingDirections.reduce((mowingDirectionItems, mowingDirection) => {
        const {direction} = mowingDirection;

        if (direction === direction1 || direction === direction2 || !excludedMowingDirections.includes(direction)) {
            return mowingDirectionItems.concat({
                id: direction,
                title: translations[direction] || direction
            });
        }

        return mowingDirectionItems;
    }, []), [direction1, direction2, excludedMowingDirections, filteredMowingDirections, translations]);

    const [cuttingStyleItems, cleanupPassItems] = useDeepMemo(() => {
        const cutItems = [
            {
                id: SINGLE_CUT,
                title: translations.ONLINK_SINGLE_CUT_STYLE
            },
            {
                id: DOUBLE_CUT,
                title: translations.ONLINK_DOUBLE_CUT_STYLE
            },
            {
                id: BACKTRACK_CUT,
                title: translations.ONLINK_BACKTRACK_CUT_STYLE
            }
        ];

        if (mowingPattern?.cuttingStyle && !cutItems.some(({id}) => mowingPattern?.cuttingStyle === id)) {
            cutItems.push({
                id: mowingPattern?.cuttingStyle,
                title: translations[mowingPattern?.cuttingStyle] || mowingPattern?.cuttingStyle
            });
        }

        return [
            cutItems,
            [
                {
                    id: undefined,
                    title: translations.ONLINK_NO_CLEANUP_PASS
                },
                {
                    id: 'Tee to Green',
                    title: translations.ONLINK_TEE_TO_GREEN_CLEANUP_PASS
                },
                {
                    id: 'Green to Tee',
                    title: translations.ONLINK_GREEN_TO_TEE_CLEANUP_PASS
                },
                {
                    id: 'Contour',
                    title: translations.ONLINK_CONTOUR_CLEANUP_PASS
                },
                {
                    id: 'Clockwise',
                    title: translations.ONLINK_CLOCKWISE_CLEANUP_PASS
                },
                {
                    id: 'Counter Clockwise',
                    title: translations.ONLINK_COUNTER_CLOCKWISE_CLEANUP_PASS
                }
            ]
        ];
    }, [mowingPattern?.cuttingStyle, translations]);

    return (
        <Stack
            direction='column'
            spacing={2}
        >
            <Typography
                display='block'
                fontWeight={600}
                paddingBottom={'4px'}
                variant='h3'
            >
                {mowingPattern.area}
            </Typography>
            <div>
                <MultiSelect
                    disableSorting={true}
                    disabled={readOnly}
                    helperText={`${replaceTranslationNames(translations.LAST_PLACEHOLDER, {
                        '0': translations.ONLINK_CUT_TYPE
                    })}: ${getLastInfo(cutHistory, 'cuttingStyle')}`}
                    items={cuttingStyleItems}
                    label={translations.ONLINK_CUT_TYPE}
                    labels={{
                        placeholder: replaceTranslationNames(translations.SELECT_PLACEHOLDER, {
                            '0': translations.ONLINK_CUT_TYPE
                        }),
                        emptyList: translations.NO_ITEMS,
                        noItemMatch: translations.NO_RESULTS_FOUND
                    }}
                    loading={loading}
                    name='cutType'
                    onChange={([cuttingStyle]) => {
                        const excludedMowingDirectionsForCut = getExcludedMowingDirections(cuttingStyle);

                        if (excludedMowingDirectionsForCut.includes(direction1)) {
                            const resolvedMowingDirection = filteredMowingDirections
                                ?.find(({direction}) => !excludedMowingDirectionsForCut.includes(direction));

                            onChange('direction', resolvedMowingDirection?.direction);
                        }

                        const firstDirectionId = directionItems.some(({id}) => id === TWELVE_TO_SIX) ?
                            TWELVE_TO_SIX :
                            directionItems[0]?.id;

                        resetSelections(cuttingStyle, firstDirectionId, onChange);
                        onChange('cuttingStyle', cuttingStyle);
                    }}
                    selectedIds={[mowingPattern?.cuttingStyle]}
                    single
                />
            </div>
            <div>
                <MultiSelect
                    disabled={readOnly}
                    helperText={`${replaceTranslationNames(translations.LAST_PLACEHOLDER, {
                        '0': translations.ONLINK_MOWING_DIRECTION
                    })}: ${getLastInfo(cutHistory, 'direction')}`}
                    items={directionItems}
                    label={translations.ONLINK_MOWING_DIRECTION}
                    labels={{
                        placeholder: replaceTranslationNames(translations.SELECT_PLACEHOLDER, {
                            '0': translations.ONLINK_MOWING_DIRECTION
                        }),
                        emptyList: translations.NO_ITEMS,
                        noItemMatch: translations.NO_RESULTS_FOUND
                    }}
                    loading={loading}
                    name='mowDirection'
                    onChange={(selectedIds) => onChange('direction', selectedIds[0])}
                    selectedIds={[direction1]}
                    single
                />
            </div>
            {CUTS_WITH_2_INPUTS.includes(mowingPattern?.cuttingStyle) &&
                <div>
                    <MultiSelect
                        disabled={readOnly}
                        helperText={`${replaceTranslationNames(translations.LAST_PLACEHOLDER, {
                            '0': translations.ONLINK_MOWING_DIRECTION
                        })}: ${getLastInfo(cutHistory, 'direction')}`}
                        items={directionItems}
                        label={translations.ONLINK_MOWING_DIRECTION}
                        labels={{
                            placeholder: replaceTranslationNames(translations.SELECT_PLACEHOLDER, {
                                '0': translations.ONLINK_MOWING_DIRECTION
                            }),
                            emptyList: translations.NO_ITEMS,
                            noItemMatch: translations.NO_RESULTS_FOUND
                        }}
                        loading={loading}
                        name='mowDirection2'
                        onChange={(selectedIds) => onChange('direction2', selectedIds[0])}
                        selectedIds={[direction2]}
                        single
                    />
                </div>
            }
            <div>
                <MultiSelect
                    disabled={readOnly}
                    helperText={`${replaceTranslationNames(translations.LAST_PLACEHOLDER, {
                        '0': translations.ONLINK_CLEANUP_PASS
                    })}: ${getLastInfo(cutHistory, 'cleanupPass')}`}
                    items={cleanupPassItems}
                    label={translations.ONLINK_CLEANUP_PASS}
                    labels={{
                        placeholder: replaceTranslationNames(translations.SELECT_PLACEHOLDER, {
                            '0': translations.ONLINK_CLEANUP_PASS
                        }),
                        emptyList: translations.NO_ITEMS,
                        noItemMatch: translations.NO_RESULTS_FOUND
                    }}
                    loading={loading}
                    name='cleanupPass'
                    onChange={(selectedIds) => onChange('cleanupPass', selectedIds[0])}
                    selectedIds={[mowingPattern?.cleanupPass]}
                    single
                />
            </div>
            <div>
                <Stack
                    alignItems='center'
                    direction='row'
                    spacing={2}
                >
                    <Typography
                        display='block'
                        variant='bodyMedium'
                    >
                        {translations.ONLINK_CUT_HISTORY}
                    </Typography>
                    <AssistanceTooltip
                        body={
                            <Box padding='8px'>
                                <Typography
                                    marginBottom='8px'
                                    variant='h4'
                                >{translations.ONLINK_CUT_HISTORY}</Typography>
                                {getCutHistoryTable(cutHistory, translations)}
                            </Box>
                        }
                    >
                        <IconInfoAlert/>
                    </AssistanceTooltip>
                </Stack>

            </div>
        </Stack>
    );
}

CutsAndDirectionsItem.propTypes = {
    cutHistory: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    membership: PropTypes.membership,
    mowingPattern: PropTypes.object,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    translations: PropTypes.translations
};

export default CutsAndDirectionsItem;
